<template>
  <StepShell
    :title="t('migrationV5.wait.title')"
    :intro="t('migrationV5.wait.intro')"
    hide-footer
  >
    <div class="migration-v5-wait">
      <!-- Status banner: ready (alert sent) | timeout | error -->
      <div
        v-if="errored"
        class="migration-v5-wait__banner migration-v5-wait__banner--error"
      >
        <i class="far fa-circle-exclamation" />
        <div>
          <strong>{{ t('migrationV5.wait.errorTitle') }}</strong>
          <p>{{ t('migrationV5.wait.errorBody') }}</p>
        </div>
        <SpmButton
          :label="t('migrationV5.wait.contactSupport')"
          icon="far fa-comments"
          class-style="p-button p-button-text p-button-secondary"
          @click="openSupport"
        />
      </div>

      <!-- Stages timeline: visual progression so the user feels something is
           happening even when the admin script takes a while. The values are
           cosmetic — the backend only exposes `pending | ready | none`. -->
      <ol class="migration-v5-wait__steps">
        <li
          v-for="(stage, i) in stages"
          :key="stage.key"
          class="migration-v5-wait__step"
          :class="{
            'migration-v5-wait__step--done': stage.state === 'done',
            'migration-v5-wait__step--active': stage.state === 'active',
            'migration-v5-wait__step--pending': stage.state === 'pending',
          }"
        >
          <span class="migration-v5-wait__step-marker">
            <i
              v-if="stage.state === 'done'"
              class="far fa-check"
            />
            <i
              v-else-if="stage.state === 'active'"
              class="far fa-spinner-third fa-spin"
            />
            <span v-else>{{ i + 1 }}</span>
          </span>
          <span class="migration-v5-wait__step-body">
            <span class="migration-v5-wait__step-title">{{
              t(`migrationV5.wait.stages.${stage.key}.title`)
            }}</span>
            <span class="migration-v5-wait__step-subtitle">{{
              t(`migrationV5.wait.stages.${stage.key}.subtitle`)
            }}</span>
          </span>
        </li>
      </ol>

      <!-- ── Ready screen (status === 'completed') ── -->
      <!-- Mirrors LiftOffStep's countdown pattern: shows a 10-second auto-
           redirect with a manual "go now" CTA so the user can skip the wait. -->
      <transition name="ready-fade">
        <div
          v-if="ready"
          class="migration-v5-wait__ready"
        >
          <div class="migration-v5-wait__ready-glow" />

          <span class="migration-v5-wait__ready-badge">
            <i class="far fa-sparkles" />
            {{ t('migrationV5.wait.ready.badge') }}
          </span>

          <i class="far fa-rocket-launch migration-v5-wait__ready-icon" />

          <div class="migration-v5-wait__ready-title">
            {{ t('migrationV5.wait.ready.title') }}
          </div>
          <p class="migration-v5-wait__ready-message">
            {{ t('migrationV5.wait.ready.message') }}
          </p>

          <div class="migration-v5-wait__countdown">
            <div class="migration-v5-wait__countdown-head">
              <span class="migration-v5-wait__countdown-label">
                <i class="far fa-clock" />
                {{ t('migrationV5.wait.ready.redirectingIn', { seconds: countdown }) }}
              </span>
            </div>
            <div class="migration-v5-wait__countdown-bar">
              <div
                class="migration-v5-wait__countdown-fill"
                :style="{ width: `${countdownPct}%` }"
              />
            </div>
          </div>

          <SpmButton
            :label="t('migrationV5.wait.ready.discoverCta')"
            icon="far fa-rocket-launch"
            class-style="p-button p-button-success migration-v5-wait__ready-cta"
            @click="redirectNow"
          />
        </div>
      </transition>

      <!-- Hide the generic "wait" copy once we're in the ready state — the
           countdown above already says everything the user needs to know. -->
      <template v-if="!ready">
        <p class="migration-v5-wait__hint">
          <i class="far fa-clock" />
          {{ t('migrationV5.wait.hint') }}
        </p>

        <p class="migration-v5-wait__elapsed">
          {{ t('migrationV5.wait.elapsed', { duration: elapsedLabel }) }}
        </p>
      </template>
    </div>
  </StepShell>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Gleap from 'gleap';
import StepShell from '@/components/onboarding-v2/shared/StepShell.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import {
  fetchMigrationV5Status,
  finalizeMigrationV5,
  MigrationV5Status,
} from '@/composables/migrationV5';

const POLL_INTERVAL_MS = 10_000;
// Soft "are you still here?" threshold (1 hour). Past this we surface a
// "contact support" CTA but keep polling — the admin script can take a
// while and we don't want to lock the user out if it overruns.
const SOFT_TIMEOUT_MS = 60 * 60 * 1000;
// Auto-redirect countdown after the migration completes. Mirrors the
// onboarding's LiftOffStep pattern (AUTO_REDIRECT_S = 10) so the user
// gets a beat to read the success state before the page jumps.
const AUTO_REDIRECT_S = 10;

type StageKey =
  | 'moduleConnected'
  | 'migration'
  | 'finalize';

interface Stage {
  key: StageKey;
  state: 'done' | 'active' | 'pending';
}

export default defineComponent({
  name: 'MigrationV5WaitStep',

  components: { StepShell, SpmButton },

  props: {
    canGoBack: { type: Boolean, default: false },
  },

  emits: ['next', 'back'],

  setup(_, { emit }) {
    const { t } = useI18n();

    // Default to `in_progress` so the stages render as "in flight" from
    // the very first paint. The state may transition through:
    //   in_progress → data_migrated → finalized
    // The wait step takes a different visual cue at each step:
    //   - in_progress : "Migration de votre boutique" active
    //   - data_migrated : "Finalisation" active (we kicked /finalize)
    //   - finalized : success screen + countdown + reload
    const status = ref<MigrationV5Status>('in_progress');
    const errored = ref(false);
    const startedAt = ref(Date.now());
    const elapsedMs = ref(0);

    // `ready` flips to true exactly once, on the poll tick that catches
    // status === 'finalized'. It drives the success screen + countdown
    // (mirrors LiftOffStep's `ready` ref).
    const ready = ref(false);
    const countdown = ref(AUTO_REDIRECT_S);
    const countdownPct = computed(
      () => Math.max(0, (countdown.value / AUTO_REDIRECT_S) * 100),
    );

    // Guards against re-calling /finalize on every 10s tick. Set to true
    // the moment we fire the call; reset only on /finalize failure so
    // the next tick can retry.
    let finalizeInFlight = false;

    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let tickTimer: ReturnType<typeof setInterval> | null = null;
    let countdownTimer: ReturnType<typeof setInterval> | null = null;

    /**
     * Stages timeline driven by the backend status:
     *   - in_progress      → migration active, finalize pending
     *   - data_migrated    → migration done, finalize active (we call
     *                        /finalize automatically in the background)
     *   - finalized        → all stages done; success screen takes over
     *
     * The mapping is deliberate: the user sees the "Finalisation" stage
     * actively spinning while the seeders run server-side, then it ticks
     * to done as the success screen appears.
     */
    const stages = computed<Stage[]>(() => {
      const allKeys: StageKey[] = [
        'moduleConnected',
        'migration',
        'finalize',
      ];
      const isMigrationDone =
        status.value === 'data_migrated' || status.value === 'finalized';
      const isFinalizeDone = status.value === 'finalized';
      return allKeys.map<Stage>((key) => {
        if (key === 'moduleConnected') return { key, state: 'done' };
        if (key === 'migration') {
          return { key, state: isMigrationDone ? 'done' : 'active' };
        }
        // finalize
        if (isFinalizeDone) return { key, state: 'done' };
        if (status.value === 'data_migrated') return { key, state: 'active' };
        return { key, state: 'pending' };
      });
    });

    const elapsedLabel = computed(() => {
      const totalSec = Math.floor(elapsedMs.value / 1000);
      const min = Math.floor(totalSec / 60);
      const sec = totalSec % 60;
      return min > 0 ? `${min} min ${sec}s` : `${sec}s`;
    });

    const redirectNow = () => {
      // The V5 stack must reinitialize from scratch (JWT may now resolve
      // V5, bundle_version=15, etc.). Hard-reload at `/` so the router
      // guard re-evaluates and lets the user through to the dashboard.
      window.location.replace('/');
    };

    const pollOnce = async () => {
      // Once we've entered the ready state, the only thing left is the
      // countdown — stop hitting the backend.
      if (ready.value) return;
      const next = await fetchMigrationV5Status();
      status.value = next;

      if (next === 'data_migrated' && !finalizeInFlight) {
        // The external migration script just signaled completion. Kick
        // the V5 seeders (default data source, system API key, VAPID,
        // default lists, V5 IDs cache) via POST /finalize. The status
        // flips to `finalized` on success and the next poll catches it.
        // On failure: reset the in-flight flag so the next 10s tick
        // retries — the backend is idempotent (each `ensure*` checks
        // first), so retries are safe.
        finalizeInFlight = true;
        try {
          await finalizeMigrationV5();
          // Don't read the response status here — let the next poll
          // tick own the transition to `finalized`, that keeps a single
          // path responsible for the success screen handover.
        } catch (e) {
          finalizeInFlight = false;
        }
        return;
      }

      if (next === 'finalized') {
        // Migration is officially done. Flip into the success screen
        // and let the watcher on `ready` start the 10s countdown. We
        // stop the poll/tick here (they'd be no-ops anyway with the
        // guards above, but keeping them running wastes wakeups).
        if (pollTimer) clearInterval(pollTimer);
        if (tickTimer) clearInterval(tickTimer);
        pollTimer = null;
        tickTimer = null;
        ready.value = true;
      }
    };

    // When `ready` flips to true, start the countdown — exactly like
    // LiftOffStep does on its own `ready` ref. Decrements every second;
    // on hitting 0 we redirect. The manual "Discover" CTA can also call
    // `redirectNow` directly to skip the wait.
    watch(ready, (value) => {
      if (!value) return;
      countdown.value = AUTO_REDIRECT_S;
      countdownTimer = setInterval(() => {
        countdown.value -= 1;
        if (countdown.value <= 0) {
          if (countdownTimer) clearInterval(countdownTimer);
          countdownTimer = null;
          redirectNow();
        }
      }, 1000);
    });

    const openSupport = () => {
      try {
        Gleap.open();
      } catch (e) {
        // noop
      }
    };

    onMounted(() => {
      // Stamp the "wait started at" so a refresh restarts the local clock
      // (this is fine — the duration is purely informational, the server
      // is the source of truth for the actual migration state).
      startedAt.value = Date.now();
      elapsedMs.value = 0;

      // Tick every second for the elapsed counter; soft-timeout flip after
      // 1h shows the "contact support" CTA (we keep polling regardless).
      tickTimer = setInterval(() => {
        elapsedMs.value = Date.now() - startedAt.value;
        if (!errored.value && elapsedMs.value >= SOFT_TIMEOUT_MS) {
          errored.value = true;
        }
      }, 1000);

      // First poll runs immediately to react fast if the admin script
      // happened to finish while the user was on step 1.
      pollOnce();
      pollTimer = setInterval(pollOnce, POLL_INTERVAL_MS);

      // Quietly satisfy emit binding (the wait step never emits anything;
      // back/next are wired by the parent but unused here).
      void emit;
    });

    onUnmounted(() => {
      if (pollTimer) clearInterval(pollTimer);
      if (tickTimer) clearInterval(tickTimer);
      if (countdownTimer) clearInterval(countdownTimer);
    });

    return {
      t,
      status,
      errored,
      ready,
      countdown,
      countdownPct,
      stages,
      elapsedLabel,
      openSupport,
      redirectNow,
    };
  },
});
</script>

<style lang="scss" scoped>
.migration-v5-wait {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 0.5rem 0 2rem;

  &__banner {
    display: flex;
    align-items: flex-start;
    gap: 0.85rem;
    padding: 1rem 1.1rem;
    border-radius: 12px;
    font-size: 0.92rem;

    &--error {
      background: rgba(239, 68, 68, 0.06);
      border: 1px solid rgba(239, 68, 68, 0.25);
      color: #b91c1c;

      i {
        font-size: 1.1rem;
        margin-top: 2px;
      }
      strong { display: block; margin-bottom: 0.25rem; color: #7f1d1d; }
      p { margin: 0; color: #b91c1c; line-height: 1.45; }
    }
  }

  &__steps {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
  }

  &__step {
    display: flex;
    align-items: flex-start;
    gap: 0.85rem;
    padding: 0.9rem 1rem;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    background: #fff;
    transition: background 200ms ease, border-color 200ms ease;

    &--done {
      background: rgba(148, 200, 64, 0.06);
      border-color: rgba(148, 200, 64, 0.35);
    }
    &--active {
      background: rgba(96, 165, 250, 0.06);
      border-color: rgba(96, 165, 250, 0.35);
    }
    &--pending {
      opacity: 0.55;
    }
  }

  &__step-marker {
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
    font-weight: 700;
    background: #e2e8f0;
    color: #64748b;

    .migration-v5-wait__step--done & {
      background: #94c840;
      color: #fff;
    }
    .migration-v5-wait__step--active & {
      background: #60a5fa;
      color: #fff;
    }
  }

  &__step-body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    line-height: 1.4;
  }

  &__step-title {
    font-weight: 600;
    color: #0f172a;
    font-size: 0.95rem;
  }

  &__step-subtitle {
    color: #64748b;
    font-size: 0.82rem;
  }

  &__hint,
  &__elapsed {
    margin: 0;
    font-size: 0.85rem;
    color: #64748b;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__hint i {
    color: #94c840;
  }

  /* ─── Ready screen (status === completed) ─── */
  &__ready {
    position: relative;
    text-align: center;
    padding: 1.75rem 1.5rem 1.5rem;
    background: linear-gradient(135deg, #f5faeb 0%, #ffffff 60%);
    border: 1px solid #94c840;
    border-radius: 1rem;
    overflow: hidden;
    box-shadow:
      0 10px 30px -12px rgba(148, 200, 64, 0.25),
      0 2px 6px -2px rgba(15, 23, 42, 0.04);
    margin-top: 0.5rem;
  }

  &__ready-glow {
    position: absolute;
    top: -40%;
    left: 50%;
    transform: translateX(-50%);
    width: 320px;
    height: 320px;
    background: radial-gradient(circle, rgba(148, 200, 64, 0.3) 0%, transparent 65%);
    filter: blur(24px);
    pointer-events: none;
    animation: glow-pulse 3.5s ease-in-out infinite;
  }

  &__ready-badge {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.25rem 0.7rem;
    margin-bottom: 0.8rem;
    background: linear-gradient(135deg, #94c840, #5d8a24);
    color: #ffffff;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    border-radius: 999px;
    box-shadow: 0 4px 10px -2px rgba(148, 200, 64, 0.4);

    i { font-size: 0.72rem; }
  }

  &__ready-icon {
    position: relative;
    display: block;
    font-size: 2.25rem;
    color: #5d8a24;
    margin-bottom: 0.65rem;
    animation: lift 2s ease-in-out infinite;
  }

  &__ready-title {
    position: relative;
    font-size: 1.25rem;
    font-weight: 800;
    color: #0f172a;
    margin-bottom: 0.35rem;
    letter-spacing: -0.01em;
  }

  &__ready-message {
    position: relative;
    margin: 0 auto 1.2rem;
    font-size: 0.88rem;
    color: #475569;
    line-height: 1.55;
    max-width: 26rem;
  }

  /* ─── Countdown ─── */
  &__countdown {
    position: relative;
    max-width: 26rem;
    margin: 0 auto;
    padding: 0.75rem 0.9rem;
    background: rgba(255, 255, 255, 0.65);
    border: 1px solid rgba(148, 200, 64, 0.3);
    border-radius: 0.6rem;
    backdrop-filter: blur(8px);
  }

  &__countdown-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.55rem;
  }

  &__countdown-label {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.78rem;
    font-weight: 600;
    color: #2d5016;

    i { font-size: 0.8rem; color: #5d8a24; }
  }

  &__countdown-bar {
    width: 100%;
    height: 4px;
    background: rgba(148, 200, 64, 0.15);
    border-radius: 999px;
    overflow: hidden;
  }

  &__countdown-fill {
    height: 100%;
    background: linear-gradient(to right, #94c840, #5d8a24);
    border-radius: 999px;
    transition: width 1s linear;
  }

  &__ready-cta {
    position: relative;
    margin-top: 1.25rem;
  }
}

/* ─── Animations (shared with LiftOffStep visual language) ─── */
@keyframes glow-pulse {
  0%, 100% { opacity: 0.7; transform: translateX(-50%) scale(1); }
  50%      { opacity: 1;   transform: translateX(-50%) scale(1.08); }
}

@keyframes lift {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-4px); }
}

.ready-fade-enter-active { transition: opacity 320ms ease, transform 320ms ease; }
.ready-fade-leave-active { transition: opacity 200ms ease; }
.ready-fade-enter-from   { opacity: 0; transform: translateY(8px); }
.ready-fade-leave-to     { opacity: 0; }
</style>
